import { EntityKind, Interpolator, MotionDetector, RenderClock, SIXTEENTHS, SNAP_SIXTEENTHS, StepCadence, WatchedStance, characterStateOf, drinkVariantOfState, stepsWhile, type CollisionGrid, type EntityState, type Facing } from "odyssey-shared";
import { actionSound, type ActionSound } from "./audio/cues.ts";
import type { NpcListener } from "./audio/npc-sounds.ts";
import { levelReached } from "./hud/level-up.ts";
import type { CharacterFigure } from "./character.ts";
import type { FeetPoint } from "./fight/targeting.ts";
import { footingAfter, heldOffset, startFooting, type Footing } from "./remote-footing.ts";
import { gameReport, type CreatureReading } from "./report.ts";
import { HEAD_ABOVE_FEET, type CharacterVisitor } from "./world-ui/characters.ts";
import { inVisionCap } from "./world-ui/nameplates/layout.ts";
import { CLAIM_LATER, type Claimed } from "./neighbours/hold.ts";

/**
 * The time, in ms, a character first drawn after the session's first frame takes to ease in from nothing to full opacity,
 * with its world UI held back until it is whole (ADR-135 item 5): one coming back from the server's sight gate shows no
 * nameless plate and no pop. Tunable.
 */
export const SILHOUETTE_OTHER_FADE_MS = 150;

/**
 * A remote character or creature on screen: its kind, its sprite, its motion, its interpolated position in fractional
 * sixteenths (the report rounds it), where its feet are drawn this frame in whole world pixels and how far its art
 * reaches above them, which world-attached UI reads (`world-ui/characters.ts`).
 */
export interface DrawnCharacter {
  readonly kind: number;
  readonly head: number;
  readonly sprite: CharacterFigure;
  readonly motion: MotionDetector;
  /** A player's footstep cadence (ADR-085's amendment for P1.9-T84); null for a creature, which has no steps. */
  readonly steps: StepCadence | null;
  /** A player's stance as this page derives it from its acts (ADR-094 decision 22); null for a creature. */
  readonly stance: WatchedStance | null;
  x: number;
  y: number;
  facing: number;
  drawnX: number;
  drawnY: number;
  /** The animation bits last drawn, against which a starting action sounds (ADR-085). */
  anim: number;
  /** The level its view last carried, against which a rise is a level reached (P2.4-T41); first sight sets it. */
  level: number;
  /**
   * Whether the viewer cannot see it (ADR-061's amendment for P1.9-T73): the hysteresis state `concealment.ts` keeps,
   * behind a prop or under a roof the viewer is not under, whoever is targeted.
   */
  concealed: boolean;
  /** Whether the world UI drawn for it hides: concealed and not the viewer's target. */
  hidden: boolean;
  /**
   * The scene time it was first drawn at, and whether it is still easing in over `SILHOUETTE_OTHER_FADE_MS` (ADR-135
   * item 5): its figure's opacity rises from 0 and its world UI waits until it is whole.
   */
  enteredAt: number;
  entering: boolean;
  /** The largest share of its body one prop drawn above it hid at its last cover judgement. */
  coverage: number;
  /** The largest share one prop that never fades, a building part, hid at that judgement (P1.9-T78). */
  keptCoverage: number;
  /** Whether it stood under a roof the viewer is not under at its last judgement. */
  roofed: boolean;
  /** Whether its feet stood in the local character's cone of sight at its last judgement (P1.9-T75). */
  sighted: boolean;
  /** The judgement round its coverage was last measured in, and where its feet were drawn then, in world pixels. */
  judgedRound: number;
  judgedX: number;
  judgedY: number;
  /**
   * The playback's strip count as a creature's attack strip started whose sound waits for its impact frame, -1 for none,
   * and that strip (ADR-095's amendment of 2026-10-10).
   */
  attackStarts: number;
  attackStrip: string | null;
  /** Each axis's footing while it moves (ADR-032's note of 2026-10-10). */
  readonly footX: Footing;
  readonly footY: Footing;
  /** The whole offset from the local character each axis was drawn at on the local character's footing, NaN on the ground's. */
  offX: number;
  offY: number;
  /** Its last motion across, in sixteenths a millisecond, which a crossing's hold carries it on (ADR-147 decision 15). */
  vx: number;
  /** The same down. */
  vy: number;
}

/**
 * Told of an action another player or a creature starts where it is drawn: its kind, its appearance, the action, and its
 * offset from the local character in tiles.
 */
export type ActionListener = (kind: number, appearance: number, sound: ActionSound, dx: number, dy: number) => void;

/**
 * Told of a level another player reached while the page watched them (P2.4-T41): their handle, the level reached, and
 * their offset from the local character in tiles. Never of a level first seen as they come into view.
 */
export type LevelListener = (handle: number, level: number, dx: number, dy: number) => void;

/**
 * Told of a drink another player took while the page watched them (ADR-074's amendment of 2026-10-27, P2.8-T89): their
 * handle, the drink's variant read from the animation state, and their offset from the local character in tiles. Never
 * of a state first seen as they come into view.
 */
export type DrinkListener = (handle: number, variant: string, dx: number, dy: number) => void;

/**
 * Told of another player each frame it is drawn: its cadence, where it is drawn in sixteenths of a tile, whether it
 * walks (moving, and playing neither a dash nor its death), the scene time, its offset from the local character in
 * tiles, and whether it is drawn running (moving, with its view's running bit), which takes the run's stride. The listener says whether a step falls and sounds it (`audio/footsteps.ts`).
 */
export type StepListener = (cadence: StepCadence, x: number, y: number, walking: boolean, time: number, dx: number, dy: number, running: boolean) => void;

/**
 * Every other player's character and every creature (CLIENT.md section 4, ADR-082): the snapshots recorded for
 * interpolation, the render clock that samples them behind the newest tick (`RenderClock`, steered toward the newest
 * tick less the measured arrival jitter and never run backwards, ADR-032's amendment of 2026-10-03), and a sprite per
 * character or creature in
 * view, made on first sight and destroyed when it leaves. A creature is drawn with the character sheet its appearance
 * names in the version's look table, its strips played from its animation bits as a character's are; one whose
 * appearance names no look the page holds is not drawn, and the first is logged once a session. A session starts it at
 * its welcome's tick and clears it as it ends.
 *
 * A character first drawn after the session's first frame eases in over `SILHOUETTE_OTHER_FADE_MS` (ADR-135 item 5):
 * its figure's opacity rises from nothing and its plate, bar and bubble wait until it is whole, so one coming back from
 * the server's sight gate shows no nameless plate and does not pop. One leaving is destroyed at once, with no effect, so
 * a gate removal and an interest leave look alike.
 *
 * Each other player's footsteps are told each frame it is drawn (`StepListener`, ADR-085's amendment for P1.9-T84): its
 * cadence lives with its drawn character, made on first sight and gone when it leaves the view, so a handle met again
 * starts afresh.
 *
 * Each character plays the strips its animation bits carry where it is drawn (P2.1-T4, ADR-074 decision 7): a strike,
 * a hurt and the death held, first sight playing nothing but drawing the dead lying. A move of more than 2 tiles
 * between two snapshots snaps (`Interpolator`), and the character is not drawn walking after the jump.
 *
 * A character is drawn running while it moves and the view it is drawn toward carries the running bit (ADR-116
 * decision 14; `RenderState.running`): the map server's word, never a measure of how far it moved, so a walker whose
 * inputs the map server answered in a bunch is not drawn running. Its weapon is on its back while it runs, whatever
 * its stance (decision 21).
 *
 * Another player's weapon is drawn from an act this page sees it start until 10 seconds pass without one, and put
 * away otherwise (`WatchedStance`, ADR-094 decision 22): nothing on the wire says a stance.
 *
 * A character whose sampled position lies outside `VISION_CAP_TILES` around the local character's drawn feet is not
 * drawn (ADR-061 decision 6, P1.9-T58): the map server's margin sends entities beyond the cap so they arrive before
 * they reach it, and a farther zoom must not show them. It is handled as one that left the view, so the plates, bars
 * and bubbles that read `drawn` skip it too.
 *
 * Each axis of a character is drawn on one of two footings (ADR-032's amendment of 2026-10-03 and its notes of
 * 2026-10-09 and 2026-10-10). An axis that holds still, and every axis on first sight, is drawn at its own rounded
 * pixel of the ground, so a character standing at a fractional true point stays on one pixel against the tiles however
 * the local character's sub-pixel phase moves. A moving axis takes its footing from relative motion
 * (`remote-footing.ts`): while its change keeps matching the local character's rendered change it is drawn at the
 * local character's drawn whole pixel plus the rounded offset between its sampled position and the local character's
 * rendered one, the footing the camera rounds on, so two characters walking together keep a steady offset on screen;
 * one moving otherwise, passing, crossing or walking while the local character stands, stays on the ground. A
 * character may settle by one pixel as an axis changes footing. Its plate, bar and bubble read the same point.
 */
export class RemoteCharacters {
  private interpolator: Interpolator | null = null;
  private readonly characters = new Map<number, DrawnCharacter>();
  private readonly recorded = new Map<number, EntityState>();
  private readonly clock = new RenderClock(0);
  private visitor: CharacterVisitor | null = null;
  private visitedEntities: ReadonlyMap<number, EntityState> | null = null;
  private warnedUnknown = false;
  private warnedLook = false;
  private joined = false;

  /**
   * Asked before a figure is made for an entity first drawn, with how many records the interpolator holds for it: a
   * figure a crossing holds for the same character (`neighbours/hold.ts`), which is drawn on from where its hold carried
   * it instead of a new one (ADR-147 decision 15), or `CLAIM_LATER` while a moving one waits for a second record, the
   * hold drawing it meanwhile.
   */
  public claim: ((state: EntityState, depth: number) => Readonly<Claimed<DrawnCharacter>> | typeof CLAIM_LATER | null) | null = null;

  /**
   * Whether `draw` writes the report's `renderLead`: true for the owning map's stream, false for a neighbour's edge view,
   * whose clock counts the neighbour's ticks.
   */
  public reportsLead = true;
  private readonly seen = new Set<number>();
  private renderedX = Number.NaN;
  private renderedY = Number.NaN;
  private readonly pose: { x: number; y: number; facing: Facing; moving: boolean; running: boolean } = { x: 0, y: 0, facing: 0, moving: false, running: false };

  /**
   * Builds the report's creature readings when a test reads them, so the frame allocates nothing for them.
   *
   * @returns One reading per creature drawn.
   */
  private readonly creatureReadings = (): CreatureReading[] => {
    const readings: CreatureReading[] = [];
    for (const [handle, drawn] of this.characters) {
      if (drawn.kind === EntityKind.Creature) {
        readings.push({ handle, appearance: this.appearances.get(handle) ?? -1, x: Math.round(drawn.x), y: Math.round(drawn.y), facing: drawn.facing, strip: drawn.sprite.strip });
      }
    }
    return readings;
  };

  private readonly appearances = new Map<number, number>();

  /**
   * Hands one remote character to the visitor `forEach` holds, with the state the view holds for it and its head height
   * raised by a hop's lift, so its plate rises with its head (ADR-116's third amendment); made once, so visiting the
   * characters allocates nothing.
   *
   * @param character The character on screen.
   * @param handle Its handle.
   */
  private readonly visitRemote = (character: DrawnCharacter, handle: number): void => {
    const state = this.visitedEntities?.get(handle);
    if (state !== undefined && this.visitor !== null) {
      this.visitor(handle, character.drawnX, character.drawnY, state, character.head + character.sprite.lift, character.hidden || character.entering);
    }
  };

  /**
   * Sounds a creature's attack once its strip reaches the frame its sheet names as the strike's (ADR-095's amendment of
   * 2026-10-10), as the map server lands the blow then: at once for a strip that names none or has ended, and never
   * once another strip has started or shows in its place, a hurt or a death.
   *
   * @param character The creature, its attack's sound waiting.
   * @param state Its view, for its kind and appearance.
   * @param dx Its offset from the local character across, in tiles.
   * @param dy The same down.
   */
  private soundAtImpact(character: DrawnCharacter, state: EntityState, dx: number, dy: number): void {
    const playback = character.sprite.playback;
    const strip = playback.strip;
    if (playback.starts !== character.attackStarts || (strip !== null && strip !== character.attackStrip)) {
      character.attackStarts = -1;
      return;
    }
    const impact = strip === null ? null : character.sprite.impactOf(strip);
    if (impact === null || character.sprite.frame >= impact) {
      character.attackStarts = -1;
      this.onAction?.(state.kind, state.appearance, "attack", dx, dy);
    }
  }

  /**
   * @param create Makes the sprite for a character or creature first seen, null when the page cannot draw it.
   * @param headOf How far a character's or creature's art reaches above its feet, in world pixels, asked once as it is
   *   first drawn; the plain figure's `HEAD_ABOVE_FEET` for every one when omitted.
   * @param onAction Told of each attack, hurt or death a drawn character or creature starts, never of what first sight
   *   shows (ADR-085 decision 2).
   * @param now The time in ms that snapshot arrivals and frames are measured on; `performance.now` when omitted.
   * @param onStep Told of every other player drawn, each frame, for its footsteps; no one when omitted.
   * @param onLevel Told of each level another player reaches while drawn (P2.4-T41); no one when omitted.
   * @param onDrink Told of each drink another player takes while drawn (P2.8-T89); no one when omitted.
   * @param onNpc Told of every NPC drawn, each frame, for its idle sounds (P2.22-T36); no one when omitted.
   */
  public constructor(
    private readonly create: (state: EntityState) => CharacterFigure | null,
    private readonly headOf: (state: EntityState) => number = () => HEAD_ABOVE_FEET,
    private readonly onAction: ActionListener | null = null,
    private readonly now: () => number = () => performance.now(),
    private readonly onStep: StepListener | null = null,
    private readonly onLevel: LevelListener | null = null,
    private readonly onDrink: DrinkListener | null = null,
    private readonly onNpc: NpcListener | null = null,
  ) {}

  /**
   * Whether a session has started interpolating.
   *
   * @returns True between a welcome and the session's end.
   */
  public get active(): boolean {
    return this.interpolator !== null;
  }

  /**
   * The characters drawn, by handle.
   *
   * @returns The characters.
   */
  public get drawn(): ReadonlyMap<number, DrawnCharacter> {
    return this.characters;
  }

  /**
   * Starts a session's interpolation over the grid, its render clock at the welcome's tick.
   *
   * @param grid The collision grid the interpolator moves against.
   * @param tick The welcome's tick.
   */
  public begin(grid: CollisionGrid, tick: number): void {
    this.interpolator = new Interpolator(grid);
    this.clock.reset(tick);
    this.joined = false;
  }

  /**
   * Destroys every character drawn and forgets what was recorded, as a session ends.
   */
  public clear(): void {
    for (const character of this.characters.values()) {
      character.sprite.destroy();
    }
    this.characters.clear();
    this.appearances.clear();
    this.recorded.clear();
    this.interpolator = null;
    this.warnedUnknown = false;
    this.warnedLook = false;
  }

  /**
   * Records an applied snapshot's other entities for interpolation. An entity whose state reference is unchanged
   * since its last record (unchanged, or omitted from the delta by tier throttling, ADR-034) is not re-recorded, so
   * it interpolates across the gap instead of freezing and jumping; the interpolator keeps its newest view however
   * old. An entity that left the view is forgotten there, so its handle entering again starts afresh. The render
   * clock is told when the tick arrived.
   *
   * @param tick The snapshot's tick.
   * @param entities The client's view.
   * @param localHandle The local player's handle, which is not recorded.
   */
  public record(tick: number, entities: ReadonlyMap<number, EntityState>, localHandle: number): void {
    const interpolator = this.interpolator;
    if (interpolator === null) {
      return;
    }
    for (const handle of this.recorded.keys()) {
      if (!entities.has(handle)) {
        this.recorded.delete(handle);
        interpolator.forget(handle);
      }
    }
    const others: EntityState[] = [];
    for (const state of entities.values()) {
      if (state.handle !== localHandle && this.recorded.get(state.handle) !== state) {
        others.push(state);
        this.recorded.set(state.handle, state);
      }
    }
    interpolator.record(tick, others);
    this.clock.observe(tick, this.now());
  }

  /**
   * Advances the render clock by a frame and poses every other player and every creature in view at the clock's sample.
   * Each axis whose sample moved since the last frame is drawn at the local character's drawn pixel plus the rounded
   * offset from its rendered position; each axis that held still, and both on first sight, at the sample's own rounded
   * pixel (the class's footing rule). One walks while its sampled position changed within the motion hold (ADR-036
   * item 3). One no longer in view, or outside the vision cap around the local character, is destroyed. The report's
   * `renderLead` takes the clock's lead over the newest tick.
   *
   * @param delta Frame delta in ms.
   * @param time Scene time in ms.
   * @param scale World pixels per sixteenth.
   * @param entities The client's view.
   * @param localHandle The local player's handle, which is not drawn here.
   * @param localX Where the local character's feet are drawn this frame, across, in whole world pixels.
   * @param localY The same down.
   * @param renderedX Where the local character is rendered this frame before rounding, across, in sixteenths;
   *   `localX / scale` when omitted.
   * @param renderedY The same down.
   */
  public draw(delta: number, time: number, scale: number, entities: ReadonlyMap<number, EntityState>, localHandle: number, localX: number, localY: number, renderedX = localX / scale, renderedY = localY / scale): void {
    const interpolator = this.interpolator;
    if (interpolator === null) {
      return;
    }
    const clock = this.clock.advance(delta, this.now());
    const tile = scale * SIXTEENTHS;
    if (this.reportsLead) {
      gameReport.renderLead = clock - this.clock.newest;
    }
    const seen = this.seen;
    seen.clear();
    for (const state of entities.values()) {
      if (state.handle === localHandle || (state.kind !== EntityKind.Player && state.kind !== EntityKind.Creature && state.kind !== EntityKind.Npc)) {
        continue;
      }
      const at = interpolator.sample(clock, state.handle);
      let character = this.characters.get(state.handle);
      const onLocalX = at !== null && character !== undefined && at.x !== character.x && footingAfter(character.footX, at.x - character.x, renderedX - this.renderedX).local;
      const onLocalY = at !== null && character !== undefined && at.y !== character.y && footingAfter(character.footY, at.y - character.y, renderedY - this.renderedY).local;
      const offX = at !== null && onLocalX ? heldOffset(character === undefined ? Number.NaN : character.offX, (at.x - renderedX) * scale) : Number.NaN;
      const offY = at !== null && onLocalY ? heldOffset(character === undefined ? Number.NaN : character.offY, (at.y - renderedY) * scale) : Number.NaN;
      const drawnX = at === null ? 0 : onLocalX ? localX + offX : Math.round(at.x * scale);
      const drawnY = at === null ? 0 : onLocalY ? localY + offY : Math.round(at.y * scale);
      if (at !== null && !inVisionCap(drawnX - localX, drawnY - localY)) {
        continue;
      }
      seen.add(state.handle);
      if (at === null) {
        continue;
      }
      if (character === undefined) {
        const adopted = this.adopt(state, interpolator.depth(state.handle));
        if (adopted === CLAIM_LATER) {
          continue;
        }
        character = adopted;
      }
      if (character === undefined) {
        const sprite = this.create(state);
        if (sprite === null) {
          if (!this.warnedLook) {
            this.warnedLook = true;
            console.warn(`creature ${state.handle} has appearance ${state.appearance}, which names no look this page can draw; drawing nothing for it`);
          }
          continue;
        }
        character = { kind: state.kind, head: this.headOf(state), sprite, motion: new MotionDetector(), steps: state.kind === EntityKind.Player && this.onStep !== null ? new StepCadence() : null, stance: state.kind === EntityKind.Player ? new WatchedStance() : null, x: at.x, y: at.y, facing: at.facing, drawnX: 0, drawnY: 0, anim: at.anim, level: state.level, concealed: false, hidden: false, enteredAt: time, entering: this.joined, coverage: 0, keptCoverage: 0, roofed: false, sighted: false, judgedRound: -1, judgedX: Number.NaN, judgedY: Number.NaN, attackStarts: -1, attackStrip: null, footX: startFooting(), footY: startFooting(), offX: Number.NaN, offY: Number.NaN, vx: 0, vy: 0 };
        this.characters.set(state.handle, character);
        this.appearances.set(state.handle, state.appearance);
      } else if (Math.abs(at.x - character.x) > SNAP_SIXTEENTHS || Math.abs(at.y - character.y) > SNAP_SIXTEENTHS) {
        character.motion.reset();
      }
      if (character.entering) {
        const eased = (time - character.enteredAt) / SILHOUETTE_OTHER_FADE_MS;
        character.entering = eased < 1;
        character.sprite.setAlpha(character.entering ? Math.max(0, eased) : 1);
      }
      if (!character.sprite.playback.observe(at.anim, time) && !this.warnedUnknown) {
        this.warnedUnknown = true;
        console.warn(`character ${state.handle} carries animation state ${characterStateOf(at.anim)}, which this page does not draw`);
      }
      if (at.anim !== character.anim) {
        const sound = this.onAction === null ? null : actionSound(character.anim, at.anim);
        character.anim = at.anim;
        const drink = state.kind === EntityKind.Player ? drinkVariantOfState(characterStateOf(at.anim)) : null;
        if (drink !== null) {
          this.onDrink?.(state.handle, drink, (drawnX - localX) / tile, (drawnY - localY) / tile);
        }
        if (sound === "attack" && (state.kind === EntityKind.Creature || state.kind === EntityKind.Npc)) {
          character.attackStarts = character.sprite.playback.starts;
          character.attackStrip = character.sprite.playback.strip;
        } else if (sound !== null) {
          this.onAction?.(state.kind, state.appearance, sound, (drawnX - localX) / tile, (drawnY - localY) / tile);
        }
      }
      if (state.level !== character.level) {
        const reached = state.kind === EntityKind.Player ? levelReached(character.level, state.level) : null;
        character.level = state.level;
        if (reached !== null) {
          this.onLevel?.(state.handle, reached, (drawnX - localX) / tile, (drawnY - localY) / tile);
        }
      }
      if (character.stance !== null) {
        character.sprite.drawn = character.stance.observe(at.anim, time);
      }
      const moving = character.motion.update(at.x, at.y, time);
      const pose = this.pose;
      pose.x = drawnX;
      pose.y = drawnY;
      pose.facing = at.facing;
      pose.moving = moving;
      pose.running = at.running;
      character.sprite.setPose(pose, time);
      if (character.attackStarts >= 0) {
        this.soundAtImpact(character, state, (drawnX - localX) / tile, (drawnY - localY) / tile);
      }
      state.kind === EntityKind.Npc && this.onNpc?.(state.handle, state.appearance, time, (drawnX - localX) / tile, (drawnY - localY) / tile);
      if (character.steps !== null) {
        this.onStep?.(character.steps, at.x, at.y, moving && stepsWhile(character.sprite.playback.strip), time, (drawnX - localX) / tile, (drawnY - localY) / tile, moving && at.running);
      }
      if (delta > 0) {
        character.vx = (at.x - character.x) / delta;
        character.vy = (at.y - character.y) / delta;
      }
      character.x = at.x;
      character.y = at.y;
      character.facing = at.facing;
      character.drawnX = drawnX;
      character.drawnY = drawnY;
      character.offX = offX;
      character.offY = offY;
    }
    this.renderedX = renderedX;
    this.renderedY = renderedY;
    this.joined = true;
    for (const [handle, character] of this.characters) {
      if (!seen.has(handle)) {
        character.sprite.destroy();
        this.characters.delete(handle);
        this.appearances.delete(handle);
      }
    }
  }

  /**
   * Takes a figure a crossing holds for an entity first drawn, when `claim` has one: it is filed under the entity's handle
   * where its hold carried it, so it moves on from there with no fade and no second figure.
   *
   * @param state The entity.
   * @param depth How many records the interpolator holds for it.
   * @returns The figure; `CLAIM_LATER` while a moving one waits for a second record; undefined when none is held for it
   *   or the one held is of another kind.
   */
  private adopt(state: EntityState, depth: number): DrawnCharacter | typeof CLAIM_LATER | undefined {
    const claimed = this.claim?.(state, depth) ?? null;
    if (claimed === null) {
      return undefined;
    }
    if (claimed === CLAIM_LATER) {
      return CLAIM_LATER;
    }
    const character = claimed.figure;
    if (character.kind !== state.kind) {
      character.sprite.destroy();
      return undefined;
    }
    character.x = claimed.x;
    character.y = claimed.y;
    character.offX = Number.NaN;
    character.offY = Number.NaN;
    character.entering = false;
    character.sprite.setAlpha(1);
    this.characters.set(state.handle, character);
    this.appearances.set(state.handle, state.appearance);
    return character;
  }

  /**
   * The state last recorded for a handle, kept until the next `record` forgets an entity that left the view.
   *
   * @param handle The handle.
   * @returns The state, or undefined.
   */
  public stateOf(handle: number): EntityState | undefined {
    return this.recorded.get(handle);
  }

  /**
   * Gives up the figure drawn for a handle without destroying it, for a crossing's hold to draw on (ADR-147 decision 15):
   * the handle is forgotten here as an entity that left the view is.
   *
   * @param handle The handle.
   * @returns The figure, or undefined when none is drawn for it.
   */
  public surrender(handle: number): DrawnCharacter | undefined {
    const character = this.characters.get(handle);
    if (character === undefined) {
      return undefined;
    }
    this.characters.delete(handle);
    this.appearances.delete(handle);
    this.recorded.delete(handle);
    this.interpolator?.forget(handle);
    return character;
  }

  /**
   * Where a character or creature drawn this frame can be clicked: its feet as drawn and the top of its art, which
   * rises with a hop's lift (ADR-116's third amendment), so a click on a hopping head still finds it.
   *
   * @param handle Its handle.
   * @param out Where to write its feet and top, in world pixels.
   * @returns False when it is not drawn this frame, leaving `out` as it was.
   */
  public box(handle: number, out: FeetPoint): boolean {
    const drawn = this.characters.get(handle);
    if (drawn === undefined) {
      return false;
    }
    out.x = drawn.drawnX;
    out.y = drawn.drawnY;
    out.top = drawn.drawnY - drawn.head - drawn.sprite.lift;
    return true;
  }

  /**
   * Writes the report's remote fields: how many other players' characters are drawn, the first one's x in whole
   * sixteenths, strip and lift, and the reader of the creatures drawn.
   */
  public report(): void {
    let players = 0;
    let first: DrawnCharacter | undefined;
    for (const drawn of this.characters.values()) {
      if (drawn.kind === EntityKind.Player) {
        players += 1;
        first ??= drawn;
      }
    }
    gameReport.remote = players;
    gameReport.remoteX = first === undefined ? null : Math.round(first.x);
    gameReport.remoteStrip = first === undefined ? null : first.sprite.strip;
    gameReport.remoteLift = first === undefined ? null : first.sprite.lift;
    gameReport.creatures = this.creatureReadings;
  }

  /**
   * Hands every remote character and creature drawn to a visitor with the state the view holds for it, allocating
   * nothing.
   *
   * @param visit The visitor.
   * @param entities The client's view.
   */
  public forEach(visit: CharacterVisitor, entities: ReadonlyMap<number, EntityState>): void {
    this.visitor = visit;
    this.visitedEntities = entities;
    this.characters.forEach(this.visitRemote);
    this.visitor = null;
    this.visitedEntities = null;
  }
}
